import { FormControl, FormErrorMessage, Select, Stack } from '@chakra-ui/react'
import type { Address, FindAddressResult } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'

import { FormRow } from '@/containers/forms/FormRow'
import { useFindAddress, useFindKatUzemi, useFindStreet } from '@/hooks'

import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { SwitchFormControl } from 'BrokerTsLib/src/components'
import { useNameof } from '../hooks/useNameof'
import { Autocomplete, type AutocompleteItem } from './Autocomplete'
import { FormLabel } from './FormLabel'
import { InputGroup } from './InputGroup'

export type RuianAddressSelectorProps = {
  addressPath: string
  isDisabled?: boolean
  showIsOutsideUrbanArea?: boolean
  showOnlyLotNumber?: boolean
  separatedCityAndCityPart?: boolean
}

const RuianAddressSelector = ({ addressPath, isDisabled, showIsOutsideUrbanArea,
  showOnlyLotNumber = true, separatedCityAndCityPart = false }: RuianAddressSelectorProps) => {
  const { app } = useApp()
  const nameof = useNameof<Address>(addressPath)
  //Address
  const { setValue, register, getFieldState, watch } = useFormContext()

  const addressPointName = nameof((x) => x.addressPoint)
  const streetName = nameof((x) => x.street)
  const streetNumberName = nameof((x) => x.streetNumber)
  const cityName = nameof((x) => x.city)
  const postCodeName = nameof((x) => x.postCode)
  const cityPartName = nameof((x) => x.cityPart)
  const cityWithCityPartName = nameof((x) => x.cityWithCityPart)
  const displayedCityName = separatedCityAndCityPart ?
    nameof((x) => x.cityWithCityPart) : // v případě použití pro adresní místo majetku zobrazujeme políčko obec včetně části
    nameof((x) => x.city) // v případě adresy osob zobrazujeme pole obec, kde je obec včetně části obce
  const isCatastralNumberName = nameof((x) => x.isCatastralNumber)
  const isOutsideUrbanAreaName = nameof((x) => x.isOutsideUrbanArea)
  const postCodeCuName = nameof((x) => x.postCodeCu)
  const catastralAreaCodeName = nameof((x) => x.catastralAreaCode)
  const parcelNumberName = nameof((x) => x.parcelNumber)
  const isEvidenceNumberName = nameof((x) => x.isEvidenceNumber)
  const withoutStreetName = nameof((x) => x.withoutStreet)
  const parcelNumberTypeAvailableName = nameof((x) => x.parcelNumberTypeAvailable)
  const parcelNumberTypeName = nameof((x) => x.parcelNumberType)

  const { error: addressPointErrors } = getFieldState(addressPointName)
  const { error: streetErrors } = getFieldState(streetName)
  const { error: streetNumberErrors } = getFieldState(streetNumberName)
  const { error: postCodeErrors } = getFieldState(postCodeName)
  const { error: displayedCityErrors } = getFieldState(displayedCityName)
  const { error: postCodeCuErrors } = getFieldState(postCodeCuName)
  const { error: catastralAreaCodeErrors } = getFieldState(catastralAreaCodeName)
  const { error: parcelNumberErrors } = getFieldState(parcelNumberName)
  const { error: parcelNumberTypeErrors } = getFieldState(parcelNumberTypeName)

  const street = watch(streetName)
  const streetNumber = watch(streetNumberName)
  const postCode = watch(postCodeName)
  const onlyLotNumberValue = watch(isCatastralNumberName)
  const katUzemi = watch(postCodeCuName)
  const isEvidenceNumber = watch(isEvidenceNumberName)
  const oddeleneRady = watch(parcelNumberTypeAvailableName)
  const addressPointValue = watch(addressPointName)

  const { refetch: refetchStreets } = useFindStreet(street, { enabled: false })
  const { mutateAsync: refetchAddress, data: addressData } = useFindAddress()

  const [validKatUzemiPostCode, setValidKatUzemiPostCode] = useState(false)
  const { data: katUzemiData } = useFindKatUzemi(katUzemi, { enabled: validKatUzemiPostCode })
  const katUzemiDataSource = validKatUzemiPostCode ? (katUzemiData?.data ?? []) : []

  const handleCatastralAreaChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setValue(parcelNumberTypeAvailableName, undefined)
    setValue(parcelNumberTypeName, undefined)
    if (event.target.value && katUzemiDataSource) {      
      const item = katUzemiDataSource.find(k => k.id === Number(event.target.value))
      if (item) {
        setValue(cityName, item.obecNazev)
        setValue(parcelNumberTypeAvailableName, item.oddeleneRady)
      }
    }
  }

  useEffect(() => {
    setValidKatUzemiPostCode(katUzemi?.length == 5 && !postCodeCuErrors)
  }, [katUzemi, postCodeCuErrors])

  const [firstLoad, setFirstLoad] = useState(true)
  const showStreet = (!!addressPointValue && !!street) || !isDisabled

  const handleRefetchAddress = (): Promise<AutocompleteItem[]> => {
    return new Promise((resolve, reject) => {
      refetchAddress({ street: street, searchQuery: streetNumber })
        .then((data) => {
          // pri prvnim nacteni dat zkusim predvybrat
          if (firstLoad && !addressPointValue) {
            const base = data ?? []

            // případné zúžení podle PSČ
            const narrowed = base.length > 1
              ? base.filter(x => String(x.psc) === String(postCode))
              : base

            const address = narrowed.length === 1 ? narrowed[0] : null

            if (address) {
              handleOnStreetNumberSelect({
                inputText: address.cisloDomovni ?? address.cisloDomovni ?? '',
                itemText: address.adresa,
                value: address.adresniMisto?.toString() ?? '',
                customData: address,
              })
            }
            else if (app == Apps.publicPoleas) {
              // pro public poleas donutime klienta vybrat adresni misto. To udelam tak, ze smazu cislo popisne a necham ho vybrat znovu
              setValue(streetNumberName, '')
            }
          }

          setFirstLoad(false)

          const sorted = (data ?? [])
            .sort((x, y) => x.obec.localeCompare(y.obec))
            .map((x) => ({
              inputText: x.cisloDomovni ?? x.cisloDomovni ?? '',
              itemText: x.adresa,
              value: x.adresniMisto?.toString() ?? '',
              customData: x,
            }))

          resolve(sorted)
        }
        )
        .catch((e) => reject(e))
    })
  }

  const handleOnStreetNumberSelect = (item: AutocompleteItem) => {
    const adresa = item.customData as FindAddressResult

    const newStreetNumber = adresa.cisloOrientacni
      ? `${adresa.cisloDomovni}/${adresa.cisloOrientacni}`
      : adresa.cisloDomovni

    let cityWithPart = adresa.obec
    if (adresa.castObce && adresa.castObce !== adresa.obec)
      cityWithPart += ' - ' + adresa.castObce

    setValue(streetNumberName, newStreetNumber ?? '')
    setValue(postCodeName, adresa.psc)
    if (separatedCityAndCityPart) {
      // v režimu adresního místa pro majetek musí být samotná obec bez části obce, jinak přestane fungovat navazující logika!!!!
      setValue(cityName, adresa.obec ?? '')
      // pro adresní místa majetku nesmí být v ulici část obce !!!
      setValue(streetName, adresa.ulice ?? '')
    }
    else {
      // pro adresy osob je v obci o část obce
      setValue(cityName, cityWithPart)
      // pro adresy osob může být v ulici část obce
      setValue(streetName, adresa.ulice ?? adresa.castObce ?? '')
    }
    setValue(cityWithCityPartName, cityWithPart)
    setValue(cityPartName, adresa.castObce ?? '')
    setValue(
      addressPointName,
      (adresa.adresniMisto ?? '').toString().trim() == ''
        ? null
        : adresa.adresniMisto,
    ) // obcas to padalo na chybu (nepodarilo se nasimulovat), tak preventive rusim moznost prazdenoho stringu - "property.data.address.addressPoint must be a `number` type, but the final value was: `NaN` (cast from the value `""`)."
    setValue(isEvidenceNumberName, adresa.typSo == 'č.ev.')
    setValue(withoutStreetName, !adresa.ulice)
  }


  return (
    <>
      {(showOnlyLotNumber || showIsOutsideUrbanArea) &&
        <Stack>
          {showOnlyLotNumber &&
            <SwitchFormControl name={isCatastralNumberName}
              label="Mám pouze parcelní číslo" />
          }

          {showIsOutsideUrbanArea &&
            <SwitchFormControl name={isOutsideUrbanAreaName}
              label="Místo se nachází mimo obec"
              labelProps={{
                hint: 'Pro pojišťovnu Allianz: místo pojištění se nachází mimo obec.'
              }} />
          }
        </Stack>
      }

      {!onlyLotNumberValue && (
        <>
          <FormRow itemsCount={showStreet ? 2 : 3}>
            {showStreet && (
              <FormControl isRequired isInvalid={!!addressPointErrors || !!streetErrors}>
                <FormLabel>Ulice</FormLabel>

                <Autocomplete
                  inputMode="text"
                  htmlSize={32}
                  validFromLength={2}
                  value={street ?? ''}
                  placeholder="Např. Hlavní"
                  isDisabled={isDisabled}
                  initLoad={!!street}
                  errors={addressPointErrors || streetErrors}
                  onChange={(value) => {
                    setValue(streetName, value)

                    if (value != street) {
                      setValue(streetNumberName, '')
                      setValue(postCodeName, '')
                      setValue(cityName, '')
                      setValue(cityWithCityPartName, '')
                      setValue(cityPartName, '')
                      setValue(addressPointName, null)
                      setValue(withoutStreetName, false)
                    }
                  }}
                  refetch={() => {
                    return new Promise((resolve, reject) => {
                      refetchStreets()
                        .then(({ data }) =>
                          resolve(data?.data.map((x) => ({ inputText: x, value: x })) ?? []),
                        )
                        .catch((e) => reject(e))
                    })
                  }}
                />
              </FormControl>
            )}

            <FormControl isRequired isInvalid={!!streetNumberErrors}>
              <FormLabel>
                {isEvidenceNumber ? 'Číslo evidenční' : 'Číslo popisné nebo orientační'}
              </FormLabel>

              <Autocomplete
                inputMode="text"
                htmlSize={8}
                value={streetNumber ?? ''}
                placeholder="Např. 1234"
                isDisabled={isDisabled}
                errors={streetNumberErrors}
                initLoad={!!street}
                onChange={(value) => {
                  setValue(streetNumberName, value)
                }}
                onSelect={handleOnStreetNumberSelect}
                refetch={handleRefetchAddress}
              />
            </FormControl>

            <FormControl isInvalid={!!postCodeErrors}>
              { /* tooltip="Zadejte poštovní směrovací číslo obce. Údaj lze nalézt na webových stránkách www.psc.cz." */}
              <FormLabel >
                PSČ
              </FormLabel>

              <InputGroup
                htmlSize={6}
                autoComplete="postal-code"
                placeholder="Např. 110 00"
                isDisabled={true}
                {...register(postCodeName)}
                errors={postCodeErrors}
              />
            </FormControl>

            {/* Obec musí být včetně části!!! */}
            <FormControl isInvalid={!!displayedCityErrors}>
              <FormLabel>Obec</FormLabel>

              <InputGroup {...register(displayedCityName)} isDisabled={true} />

              {displayedCityErrors && (
                <FormErrorMessage>{displayedCityErrors.message}</FormErrorMessage>
              )}
            </FormControl>
          </FormRow>
        </>
      )}

      {onlyLotNumberValue && (
        <>
          <FormRow>
            <FormControl isInvalid={!!postCodeCuErrors}>
              <FormLabel tooltip="Zadejte poštovní směrovací číslo obce. Údaj lze nalézt na webových stránkách www.psc.cz.">
                PSČ
              </FormLabel>

              <InputGroup
                htmlSize={6}
                autoComplete="postal-code"
                placeholder="Např. 110 00"
                isDisabled={isDisabled}
                {...register(postCodeCuName)}
                errors={postCodeCuErrors}
              />
            </FormControl>

            <FormControl isInvalid={!!catastralAreaCodeErrors}>
              <FormLabel tooltip="Katastrální území">Katastrální území</FormLabel>

              {(katUzemiDataSource?.length ?? 0) > 0 && (
                <Select {...register(catastralAreaCodeName)} isDisabled={isDisabled} onChange={handleCatastralAreaChange}>
                  <option disabled value="">
                    Vyberte katastrální území
                  </option>
                  {katUzemiDataSource.map((item) => (
                    <option value={item.id} key={item.id}>
                      {item.nazev}
                    </option>
                  ))}
                </Select>
              )}
              {(katUzemiDataSource?.length ?? 0) == 0 && (
                <Select isDisabled={true} defaultValue="">
                  <option disabled value="">
                    Vyberte katastrální území
                  </option>
                </Select>
              )}

              {catastralAreaCodeErrors && (
                <FormErrorMessage>{catastralAreaCodeErrors.message}</FormErrorMessage>
              )}
            </FormControl>

            {oddeleneRady && (
              <FormControl isInvalid={!!parcelNumberTypeErrors}>
                <FormLabel>Typ parcely</FormLabel>

                <Select {...register(parcelNumberTypeName)} 
                  value={watch(parcelNumberTypeName)?.toString() ?? ''}
                  onChange={evnt => setValue(parcelNumberTypeName, evnt.target.value ? Number(evnt.target.value) : undefined)}
                  isDisabled={isDisabled}>
                  <option disabled value="">
                    Vyberte typ parcely
                  </option>                 
                  <option value={'1'} key={1}>
                    Stavební
                  </option>
                  <option value={'2'} key={2}>
                    Pozemková
                  </option>                
                </Select>
              
                {parcelNumberTypeErrors && (
                  <FormErrorMessage>{parcelNumberTypeErrors.message}</FormErrorMessage>
                )}
              </FormControl>
            )}

            <FormControl isInvalid={!!parcelNumberErrors}>
              <FormLabel>Parcelní číslo</FormLabel>

              <InputGroup
                htmlSize={32}
                {...register(parcelNumberName)}
                errors={parcelNumberErrors}
                isDisabled={isDisabled}
              />
            </FormControl>
          </FormRow>
        </>
      )}
    </>
  )
}

export default RuianAddressSelector
